/**
 * The pending-mutation record, and the rules for folding a new change into one
 * already waiting. Pure — every function here is a value in and a value out, so
 * the decision table below is testable exhaustively without a database.
 *
 * **There are two kinds of record, and which one a change becomes is decided
 * when it is queued** (g107), from what the server last said:
 *
 * - **A whole action**, while the server records history. Every committed
 *   save, tick, untick, move and delete is a record of its own, with its own
 *   operation id and the device's time, and nothing is ever folded into it or
 *   dropped because of it. It carries `actionVersion`, and is sent with an
 *   `action` envelope that never changes between attempts. The queue is then
 *   bounded by the actions made, which is the cost of remembering each one.
 * - **A folded record**, while it does not. Ordinary edits coalesce per entity
 *   id: five taps on one ordinary checkbox are one patch, not five, so the
 *   queue is bounded by the number of things touched. Recurrence commands and
 *   edits keep separate ordered records. Once sent, receipt-bearing records
 *   are immutable even when a response is lost.
 *
 * A folded record still waiting when the server starts recording stays as it
 * is and is sent without an envelope, so it is recorded as one action that
 * says it was coalesced. Nothing folds into a whole action, and a whole action
 * folds into nothing.
 */

import type { Item, List } from "../lib/api.js";
import type { Anchors, ItemPatch, ListOrder, ListPatch } from "../lib/api.js";
import type { Mutation } from "./transport.js";
import { MAX_BATCH, MAX_SYNC_BODY_BYTES } from "./transport.js";
import type { ImportMutation } from "./imports.js";
import { ACTION_META_VERSION, queuedCoverage, type ActionMetaInput, type Coverage } from "../../api/src/shared/activity.js";

export type Entity = "list" | "item";
export type QueueOp = "upsert" | "patch" | "delete" | "move" | "complete" | "undo" | "import";

/**
 * A whole row for an upsert, the changed fields for a patch, nothing for a
 * delete. Untyped because it comes back out of IndexedDB opaque either way, and
 * a type that only holds on the way in is a type that lies.
 */
export type Fields = Record<string, unknown>;

/**
 * What the store captures for every committed change (g107): an id for the
 * command and when the device made it. It becomes the record's own only while
 * the server records; otherwise it is dropped, and the record is as it was
 * before g107. The time and zone come together or not at all.
 */
export interface Captured {
  operationId: string;
  occurredAt?: number;
  timeZone?: string;
}

/** A record's place in a group of one user action (g107), sent in its envelope (g108). */
export interface ActionGroup {
  id: string;
  index: number;
  /** How many children the group expects. */
  size: number;
}

/** The device's time and zone for a change made now, or neither where the browser names no usable zone. */
export function deviceClock(now: number = Date.now()): { occurredAt?: number; timeZone?: string } {
  let timeZone: string | undefined;
  try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { timeZone = undefined; }
  // The server refuses a zone it does not know, and a refused field refuses the whole batch.
  if (!timeZone || timeZone.length > 64 || timeZone === "Etc/Unknown") return {};
  return { occurredAt: now, timeZone };
}

export interface Draft {
  /** See `Captured`. Never stored: `whole` or `folded` takes it off. */
  captured?: Captured;
  /** Whose queue this was written to (g107): the engine sends a record only as the actor that queued it. */
  actorScope?: string;
  group?: ActionGroup;
  /**
   * The operation this change puts back (g108): Undo move names the move it
   * undoes. Kept on a whole action and sent in its envelope.
   */
  reverses?: string;
  blockedBy?: string[];
  operationId?: string;
  expectedRevision?: number;
  /** Each occurrence-related edit is an ordering barrier, including unsent ones. */
  ordered?: boolean;
  entity: Entity;
  id: string;
  op: QueueOp;
  /** An item's parent list; `null` for a list. Indexed, so a list delete can sweep. */
  listId: string | null;
  fields: Fields;
  /**
   * The row as it stood before the *first* change in this chain — the revert
   * source when the server refuses. Captured once and never overwritten, so a
   * create followed by five patches reverts in one step rather than five.
   */
  before: List | Item | null;
}

export interface Pending extends Omit<Draft, "captured"> {
  /**
   * Set on a whole action (g107), and what makes it one: immutable from the
   * moment it is queued, never folded into, and sent with `action`.
   */
  actionVersion?: typeof ACTION_META_VERSION;
  /** When the device made the change, and where. Both or neither. */
  occurredAt?: number;
  timeZone?: string;
  /** Assigned by IndexedDB's autoIncrement. The queue's order. */
  seq: number;
  key: string;
  /**
   * On the wire *right now*, and therefore frozen: a verdict is coming that
   * names this `seq`, and folding a later edit in would see that edit deleted
   * along with the record. Cleared the moment an attempt ends without verdicts.
   */
  inflight: boolean;
  /**
   * Has been on the wire at least once, ever. Never cleared. It answers exactly
   * one question — could the server already hold this row? — and that question
   * has exactly one caller, below.
   */
  sent: boolean;
  /**
   * A shared scope's link stopped working before this reached the server
   * (g71). Never sent again, and never folded into: it is kept only so the
   * person can see what did not go.
   */
  unsent?: string;
}

export type Coalesced =
  | { action: "insert"; record: Omit<Pending, "seq"> }
  | { action: "update"; record: Pending }
  | { action: "drop"; seq: number }
  | { action: "keep" };

/**
 * A move gets its own key, and therefore its own pending record.
 *
 * Ordinary field edits coalesce into one record, which is what keeps a
 * create-then-edit-then-edit from becoming three mutations. A move cannot join
 * them: it is a different mutation carrying different arguments, and folding it
 * in would mean one record had to be two things. Kept apart, a queued edit and a
 * queued move both survive, and `seq` still sends them in the order they
 * happened.
 *
 * Nothing rebases against this key -- `mirror.ts` looks up the base one -- which
 * is correct, because a move carries no field a delta could conflict with.
 */
/**
 * A change to how a list is ordered (g81): a list patch carrying `orderBy`
 * and nothing else. It never coalesces and always travels alone, last among
 * what was queued before it, so a list made offline has its items on the
 * server before the switch fills their categories.
 */
export function isListOrderChange(record: Pick<Draft, "entity" | "op" | "fields">): boolean {
  return record.entity === "list" && record.op === "patch" && "orderBy" in record.fields;
}

/**
 * What a waiting record is to activity history (g103). A whole action (g107)
 * has its `actionVersion`, its own id and its time, and is recorded in full.
 * A folded record has no `actionVersion`, and an upsert, patch, move or
 * delete may already hold several taps folded into one, which nothing can
 * take apart again. So it will be recorded as one action that says it was
 * coalesced, never as the taps it was. An `ordered` record under its own
 * operation id is the exception: `coalesce` never folds into one, so it is
 * whole. A record keeps its answer for as long as it waits. No store changes
 * for this, and `DB_VERSION` stays where it is.
 */
export function pendingCoverage(record: Pick<Pending, "op" | "operationId" | "ordered"> & { actionVersion?: number }): Coverage {
  return queuedCoverage(record);
}

/** A draft as a whole action: under its own operation id, with the device's time. Used while the server records. */
export function whole(draft: Draft): Draft & { actionVersion?: typeof ACTION_META_VERSION; occurredAt?: number; timeZone?: string } {
  const { captured, ...rest } = draft;
  if (!captured) return rest;
  const timed = captured.occurredAt !== undefined && captured.timeZone !== undefined;
  return {
    ...rest,
    actionVersion: ACTION_META_VERSION,
    // A command that already had an id of its own keeps it: a completion's is its undo token.
    operationId: rest.operationId ?? captured.operationId,
    ...(timed ? { occurredAt: captured.occurredAt, timeZone: captured.timeZone } : {}),
  };
}

/** A draft as it was queued before g107: nothing captured is kept, no group, no scope and nothing it reverses. Used while the server does not record. */
export function folded(draft: Draft): Draft {
  const { captured: _captured, group: _group, actorScope: _actorScope, reverses: _reverses, ...rest } = draft;
  return rest;
}

/** The envelope a whole action is sent with. Built from what was stored, so every attempt sends the same bytes. */
function envelope(record: Pending): { action?: ActionMetaInput } {
  if (record.actionVersion === undefined || !record.operationId) return {};
  return { action: {
    v: record.actionVersion,
    operationId: record.operationId,
    ...(record.occurredAt !== undefined && record.timeZone !== undefined ? { occurredAt: record.occurredAt, timeZone: record.timeZone } : {}),
    // Its place in the group its push declares, and the operation it puts back (g108).
    ...(record.group ? { group: { id: record.group.id, index: record.group.index } } : {}),
    ...(record.reverses !== undefined ? { reversesOperationId: record.reverses } : {}),
  } };
}

export function pendingKey(entity: Entity, id: string, op?: QueueOp): string {
  return op === "move" ? `${entity}:move:${id}` : `${entity}:${id}`;
}

/**
 * Copies only the keys that carry a value.
 *
 * `undefined` means "leave this alone" and `null` means "clear it", and a plain
 * spread does not tell them apart — `{ ...a, due: undefined }` *sets* `due`.
 * IndexedDB stores `undefined` happily, so the mistake would survive a reload
 * and only surface as a 400 that takes the whole batch down with it.
 */
export function mergeFields(base: Fields, extra: Fields): Fields {
  const merged: Fields = { ...base };
  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined) merged[key] = value;
  }
  return merged;
}

/** Drops the keys a patch has no business carrying — see `mergeFields`. */
export function compact(patch: ListPatch | ItemPatch): Fields {
  const fields: Fields = {};
  for (const [key, value] of Object.entries(patch)) {
    if (value !== undefined) fields[key] = value;
  }
  return fields;
}

/**
 * The decision table.
 *
 * | pending           | incoming | result                                   |
 * |-------------------|----------|------------------------------------------|
 * | none              | any      | insert it                                |
 * | upsert, unsent    | delete   | **drop the record; queue nothing**       |
 * | upsert, sent once | delete   | becomes a delete, in place               |
 * | upsert            | patch    | upsert, fields merged                    |
 * | patch             | patch    | patch merged, later value wins per field |
 * | patch             | delete   | delete                                   |
 * | delete            | any      | unchanged — a delete supersedes          |
 * | in flight         | any      | insert a new record                      |
 * | a whole action    | any      | insert a new record                      |
 * | any               | a whole action | insert a new record                |
 *
 * An upsert cancelled by a delete queues nothing at all *when the id has never
 * been on the wire*, which is what `sent` is for. If the create did go out and
 * the reply was lost, dropping both would leave a row in the account that
 * nothing ever cleans up, so the record becomes a delete instead — the server
 * has the row, and will tombstone it.
 *
 * An ordinary patch is frozen only while **in flight**. A
 * failed attempt releases it, so ticking a checkbox during an hour of offline
 * retries is still one patch rather than one per retry. That is the difference
 * between a queue bounded by the things touched and one bounded by the taps.
 * Receipt-bearing records stay frozen once sent; occurrence commands and
 * recurrence edits always retain their own ordered records.
 *
 * An update writes back under the **same `seq`**, which is load-bearing: create
 * a list, add an item to it, then rename the list, and the batch must still be
 * `[list.upsert, item.upsert]`. Sending the rename last would put the item
 * ahead of its parent and get it rejected.
 */
export function coalesce(existing: Pending | undefined, draft: Draft): Coalesced {
  // What was captured is taken up by `whole` or dropped by `folded`, and is no part of a stored record.
  if (draft.captured) { const { captured: _captured, ...rest } = draft; draft = rest; }
  if (existing?.blockedBy?.length) draft = { ...draft, blockedBy: [...new Set([...existing.blockedBy, ...draft.blockedBy ?? []])] };
  const fresh = {
    action: "insert" as const,
    record: {
      ...draft,
      key: draft.ordered ? `${draft.entity}:operation:${draft.operationId}` : pendingKey(draft.entity, draft.id, draft.op),
      inflight: false,
      sent: false,
    },
  };

  if (!existing || existing.inflight || existing.unsent || draft.ordered || existing.ordered || (existing.sent && existing.operationId)) return fresh;
  // A whole action is never folded into, and never folds into anything (g107).
  if (existing.actionVersion !== undefined || (draft as Partial<Pending>).actionVersion !== undefined) return fresh;

  // Already gone. Nothing said afterwards changes that, and a resurrection is
  // not something any screen offers.
  if (existing.op === "delete") return { action: "keep" };

  if (draft.op === "delete") {
    if (existing.op === "upsert" && !existing.sent) return { action: "drop", seq: existing.seq };
    return {
      action: "update",
      record: { ...existing, op: "delete", fields: {}, listId: existing.listId },
    };
  }

  if (draft.op === "upsert") {
    return { action: "update", record: { ...existing, op: "upsert", fields: draft.fields } };
  }

  // A move onto a pending create is the create landing somewhere else: the row
  // does not exist on the server yet, so there is nothing to move and the
  // anchors simply become the ones it is born between.
  if (draft.op === "move" && existing.op === "upsert") {
    return {
      action: "update",
      record: { ...existing, fields: { ...existing.fields, ...draft.fields } },
    };
  }

  // Replaced, never merged. Two anchors fully describe a position, and merging
  // a move that names only `beforeId` onto one that named only `afterId` would
  // build a third position out of two halves, neither of them current.
  if (draft.op === "move") {
    return { action: "update", record: { ...existing, op: "move", fields: draft.fields } };
  }

  return {
    action: "update",
    record: { ...existing, fields: mergeFields(existing.fields, draft.fields) },
  };
}

/**
 * The record as the server wants to hear it.
 *
 * An upsert carries the whole row, which is only ever safe for a row this
 * device created — nobody else has seen the id. Every later change is a patch,
 * because an upsert is a whole-record replace and would undo another device's
 * edit to a field this one never touched.
 */
export function toMutation(record: Pending): Mutation {
  // The envelope of a whole action (g107), on every mutation; a folded record has none.
  const action = envelope(record);
  if (record.op === "import") return { ...(record.fields as unknown as ImportMutation), ...action };
  const { entity, id, op, fields } = record;
  const context = record.operationId ? { operationId: record.operationId, expectedRevision: record.expectedRevision } : {};

  if (entity === "list") {
    if (op === "move") {
      const { afterId, beforeId } = fields as Anchors;
      return { op: "list.move", id, afterId, beforeId, ...action };
    }
    if (op === "delete") return { op: "list.delete", id, ...action };
    // Only the ordering itself: nothing else is this record's to send.
    if (isListOrderChange(record)) return { op: "list.patch", id, patch: { orderBy: (fields.orderBy ?? null) as ListOrder | null }, ...action };
    if (op === "patch") return { op: "list.patch", id, patch: fields as ListPatch, ...action };
    const row = fields as unknown as List;
    return { op: "list.upsert", id, name: row.name, kind: row.kind, ...action };
  }

  if (op === "delete") return { op: "item.delete", id, ...action };
  if (op === "complete" || op === "undo") return { op: op === "complete" ? "item.complete" : "item.undoCompletion", id,
    operationId: record.operationId!, expectedRevision: record.expectedRevision!, ...fields, ...action };
  if (op === "patch") return { op: "item.patch", id, patch: fields as ItemPatch, ...context, ...action };
  if (op === "move") {
    const { afterId, beforeId } = fields as Anchors;
    return { op: "item.move", id, afterId, beforeId, ...context, ...action };
  }
  const row = fields as unknown as Item & Anchors;
  return {
    op: "item.upsert",
    id,
    listId: row.listId,
    text: row.text,
    done: row.done,
    quantity: row.quantity,
    // A row from before g79 has no category; null says so to the server.
    category: row.category ?? null,
    note: row.note,
    due: row.due,
    ...(row.repeat !== undefined ? { repeat: row.repeat } : {}),
    ...context,
    ...action,
    // The neighbours this row was created between, recorded at the time. No
    // `orderKey`: the server assigns one and takes none.
    afterId: row.afterId,
    beforeId: row.beforeId,
  };
}

export function batch(records: Pending[], max: number = MAX_BATCH, reserve = 0): Pending[][] {
  const batches: Pending[][] = [];
  let group: Pending[] = [];
  // The envelope and the largest cursor, and room kept for the groups a push may also carry (g108).
  const empty = 128 + reserve;
  let bytes = empty;
  const flush = () => { if (group.length) batches.push(group); group = []; bytes = empty; };
  for (const record of records) {
    // The server takes these one to a request: each is charged by its size.
    if (record.op === "import" || isListOrderChange(record)) { flush(); batches.push([record]); continue; }
    const size = new TextEncoder().encode(JSON.stringify(toMutation(record))).byteLength + 1;
    if (group.length >= max || bytes + size > MAX_SYNC_BODY_BYTES) flush();
    group.push(record); bytes += size;
  }
  flush();
  return batches;
}

/**
 * Skip a throttled import and its dependents, retaining order for every touched row.
 *
 * A change to a list's ordering (g81) held back holds back everything later
 * in its list too, so nothing there overtakes it; and it waits itself behind
 * any earlier record of its list that is held back. Other lists keep draining.
 *
 * A list's delete waits in the same way (g107): its children's earlier
 * records are no longer swept away, and one sent after the delete would be
 * refused for a list that is gone.
 */
export function eligible(records: Pending[], deferred: ReadonlySet<number>): Pending[] {
  const imports = new Set(records.filter(record => record.op === "import").map(record => record.operationId!));
  const blockedRows = new Set<string>();
  const listsWithBlocked = new Set<string>();
  const blockedLists = new Set<string>();
  return records.filter(record => {
    const row = `${record.entity}:${record.id}`;
    const scope = record.entity === "list" ? record.id : record.listId;
    const ordering = isListOrderChange(record);
    const waits = ordering || (record.entity === "list" && record.op === "delete");
    const blocked = deferred.has(record.seq) || blockedRows.has(row) || record.blockedBy?.some(id => imports.has(id))
      || (scope !== null && blockedLists.has(scope)) || (waits && listsWithBlocked.has(record.id));
    if (blocked) {
      blockedRows.add(row);
      if (scope !== null) listsWithBlocked.add(scope);
      if (ordering) blockedLists.add(record.id);
    }
    return !blocked;
  });
}
